import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import google from "../../assests/Icons/google.svg";
import apple from "../../assests/Icons/apple.svg";
import discord from "../../assests/Icons/discord.svg";
import RscLogo from "../../assests/logo/rscLogo";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import { useDispatch } from "react-redux";
import {
  loginUser,
  loginUserByGoogle,
  verify2FALogin,
} from "../../reducers/authentication.slice";
import { AuthContext } from "../../context/authContext";
import "react-toastify/dist/ReactToastify.css";
import CustomLoader from "../../components/CustomLoader";
import { migratePayout } from "../../reducers/payouts.slice";

const LoginPageWrapper = styled(Stack)(({ theme }) => ({
  minHeight: "100vh",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000000",
  overflow: "none",
}));

const LoginPageComponent = styled(Stack)(({ theme }) => ({
  padding: "24px",
  backgroundColor: "#101010",
  borderRadius: "10px",
  border: "1px solid #ffffff85",
  maxHeight: "713px !important",
  width: "370px",
  [theme.breakpoints.down("md")]: {
    width: "480px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "320px",
    padding: "16px",
  },
  [theme.breakpoints.down("xs")]: {
    width: "95%",
    padding: "16px",
  },
}));

const LogoSectionWrapper = styled(Stack)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "row",
}));

const LogoDisplaySection = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "white",
  width: "fit-content",
  borderRadius: "3px",
  marginBottom: "20px",
}));

const CustomFormLabel = styled(FormLabel)(({ theme }) => ({
  color: "white",
  marginBottom: "8px",
  fontFamily: "Inter",
  letterSpacing: "0.8px",
  marginTop: "10px",
}));

const DividerSection = styled(Stack)(({ theme }) => ({
  marginTop: "35px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
}));

const Line = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff85",
  height: "1px", // Thin line
  width: "100%",
  zIndex: 1, // Make sure the line is behind the "or" text
}));

const OrBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#000000",
  borderRadius: "20px",
  padding: "5px 15px",
  zIndex: 2,
  position: "absolute",
  color: "white",
}));

const SignUpSection = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
  marginTop: "30px",
}));

const SignUpComponent = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  padding: theme.spacing(1.5),
  alignItems: "center",
  border: "1px solid #ffffff85",
  borderRadius: "10px",
  cursor: "pointer",
}));

const LogoSection = styled(Stack)(({ theme, image }) => ({
  width: "10%",
  height: "20px",
  padding: "5px",
  backgroundImage: `url(${image})`,
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
}));

const SignUpContentSection = styled(Stack)(({ theme, image }) => ({
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
}));

const SignUpAction = styled(Stack)(({ theme, image }) => ({
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
}));

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [userToken, setUserToken] = useState("");
  const dispatch = useDispatch();
  const [checked, setChecked] = useState(true);
  const { setLoginType, setUserDetails } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [localUserData, setLocalUserData] = useState(null); // Store user data locally for 2FA verification

  useEffect(() => {
    localStorage.setItem("loginType", true);
    sessionStorage.setItem("activeStep", 0);
    sessionStorage.removeItem("formData");
  }, []);

  useEffect(() => {
    if (location.state?.userData?.requires_2fa_action === "verify") {
      setLocalUserData(location.state.userData);
      setActiveStep(2);
    }
  }, [location.state]);

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  const { showToast } = useContext(AuthContext);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const handleInputChange = (e) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const value = e.target.value;
    setEmail(value);

    if (!emailRegex.test(value)) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handleCheckEmail = (e) => {
    e.preventDefault();
    if (!emailRegex.test(email)) {
      setError(true);
    } else {
      setActiveStep(1);
    }
  };

  useEffect(() => {
    localStorage.setItem("isVideo", "play");
  }, []);

  const handleLoginGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      setShowLoader(true);
      setUserToken(codeResponse.access_token);
      const payload = {
        authToken: codeResponse.access_token,
      };
      dispatch(loginUserByGoogle(payload)).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          setShowLoader(false);
          navigate("/error", { state: res?.error?.message });
          return;
        }
        setLocalUserData(res?.payload);
        if (res?.payload?.requires_2fa_action === "setup") {
          setShowLoader(false);
          navigate("/setup-2fa", { state: { userData: res.payload } });
          return;
        }
        if (res?.payload?.requires_2fa_action === "verify") {
          setShowLoader(false);
          setActiveStep(2);
          return;
        }
        setUserDetails(res?.payload);
        localStorage.setItem("user", JSON.stringify(res?.payload));

        /* CHECKING FOR WHETHER THE USER IS SUSPENDED OR NOT */
        if (res.payload?.status === "SUSPENDED") {
          setShowLoader(false);
          showToast(
            "Your account is suspended. You do not have access to log in.",
            "error"
          );
          return;
        }

        /* CHECKING FOR WHETHER THE USER IS VERIFIED OR NOT */
        if (!res?.payload?.is_verified) {
          setShowLoader(false);
          sessionStorage.setItem("activeStep", 3);
          navigate("/signup", {
            state: {
              isVerifiedCheck: true,
              email,
            },
          });
          return;
        }

        if (res?.payload?.is_discord_verified) {
          dispatch(migratePayout()).then((mig_response) => {
            if (
              mig_response?.error &&
              Object.keys(mig_response?.error)?.length > 0
            ) {
              setShowLoader(false);
              showToast(mig_response?.error?.message, "error");
            }
          });
        }

        setShowLoader(false);
        if (res?.payload?.is_verified) {
          if (res?.payload?.role === "MEMBER") {
            navigate("/user-dashboard");
          } else if (res?.payload?.role === "MANAGER") {
            navigate("/manager-dashboard");
          } else if (res?.payload?.role === "SUPERADMIN") {
            navigate("/admin-dashboard");
          } else {
            showToast("Invalid User", "error");
          }
        }
      });
    },
    onError: (error) => navigate("/error", { state: error }),
  });

  const onGoogleLoginClick = () => {
    handleLoginGoogle();
  };

  const signupaction = [
    {
      title: "Continue with Google",
      logo: google,
      action: onGoogleLoginClick,
    },
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    setShowLoader(true);
    const payload = {
      email,
      password,
      trusted_device: checked,
    };
    dispatch(loginUser(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setUserDetails(res?.payload);
      setLocalUserData(res?.payload); // Store locally for 2FA verification

      /* CHECK IF 2FA IS REQUIRED */
      if (res?.payload?.requires_2fa_action === "setup") {
        setShowLoader(false);
        // Navigate to 2FA setup page with user data (no token - APIs are open)
        navigate("/setup-2fa", { state: { userData: res?.payload } });
        return;
      }

      if (res?.payload?.requires_2fa_action === "verify") {
        setShowLoader(false);
        // Show 2FA verification step
        setActiveStep(2);
        return;
      }

      localStorage.setItem("user", JSON.stringify(res?.payload));

      /* CHECKING FOR WHETHER THE USER IS SUSPENDED OR NOT */
      if (res.payload?.status === "SUSPENDED") {
        setShowLoader(false);
        showToast(
          "Your account is suspended. You do not have access to log in.",
          "error"
        );
        return;
      }

      /* CHECKING FOR WHETHER THE USER IS VERIFIED OR NOT */
      if (!res?.payload?.is_verified) {
        setShowLoader(false);
        sessionStorage.setItem("activeStep", 3);
        navigate("/signup", {
          state: {
            isVerifiedCheck: true,
            email,
          },
        });
        return;
      }

      if (res?.payload?.is_discord_verified) {
        dispatch(migratePayout()).then((mig_response) => {
          if (
            mig_response?.error &&
            Object.keys(mig_response?.error)?.length > 0
          ) {
            setShowLoader(false);
            showToast(mig_response?.error?.message, "error");
          }
        });
      }

      setShowLoader(false);
      if (res?.payload?.is_verified) {
        if (res?.payload?.role === "MEMBER") {
          navigate("/user-dashboard");
        } else if (res?.payload?.role === "MANAGER") {
          navigate("/manager-dashboard");
        } else if (res?.payload?.role === "SUPERADMIN") {
          navigate("/admin-dashboard");
        } else {
          showToast("Invalid User", "error");
        }
      }
    });
  };

  const handleChange = (event) => {
    localStorage.setItem("loginType", event.target.checked);
    setLoginType(event.target.checked);
    setChecked(event.target.checked);
  };

  const handle2FAVerification = (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      showToast("Please enter a valid 6-digit code", "error");
      return;
    }

    setShowLoader(true);
    const payload = {
      user_id: localUserData?.id, // Use user_id from initial login response (no password re-send!)
      code: twoFACode,
      trusted_device: checked,
      two_fa_challenge: localUserData?.two_fa_challenge, // One-time code from the login response
    };

    dispatch(verify2FALogin(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message || "Verification failed", "error");
        return;
      }

      const userData = res?.payload;
      setUserDetails(userData);
      localStorage.setItem("user", JSON.stringify(userData));

      if (res?.payload?.is_discord_verified) {
        dispatch(migratePayout()).then((mig_response) => {
          if (
            mig_response?.error &&
            Object.keys(mig_response?.error)?.length > 0
          ) {
            setShowLoader(false);
            showToast(mig_response?.error?.message, "error");
          }
        });
      }

      setShowLoader(false);
      if (userData?.role === "MEMBER") {
        navigate("/user-dashboard");
      } else if (userData?.role === "MANAGER") {
        navigate("/manager-dashboard");
      } else if (userData?.role === "SUPERADMIN") {
        navigate("/admin-dashboard");
      } else {
        showToast("Invalid User", "error");
      }
    });
  };

  return (
    <>
      <CustomLoader open={showLoader} />
      <LoginPageWrapper>
        <LoginPageComponent>
          <LogoSectionWrapper>
            <LogoDisplaySection>
              <RscLogo color="black" />
            </LogoDisplaySection>
            {(activeStep === 1 || activeStep === 2) && (
              <Stack
                gap={1}
                flexDirection={"row"}
                alignContent={"center"}
                color={"#ffffff85"}
                sx={{ cursor: "pointer" }}
                onClick={() => setActiveStep(activeStep === 2 ? 1 : 0)}
              >
                <KeyboardBackspaceIcon />{" "}
                <Typography fontFamily={"inter"}>Back</Typography>
              </Stack>
            )}
          </LogoSectionWrapper>
          {activeStep === 0 ? (
            <>
              <Typography
                sx={{
                  fontSize: "1.7rem",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Log in
              </Typography>
              <form onSubmit={handleCheckEmail}>
                <FormControl fullWidth>
                  <CustomFormLabel htmlFor="lastName">Email</CustomFormLabel>
                  <TextField
                    type="email"
                    name="email"
                    value={email}
                    placeholder="Email"
                    onChange={handleInputChange}
                    error={emailError}
                    helperText={
                      emailError ? "Please enter a valid email address." : ""
                    }
                  />
                  <Button
                    disabled={email.length === 0 || emailError}
                    variant="contained"
                    type="submit"
                    sx={{ mb: 0 }}
                  >
                    Next
                  </Button>
                </FormControl>
              </form>
              <DividerSection>
                <Line />
                <OrBox>or</OrBox>
              </DividerSection>
              <SignUpSection>
                {signupaction?.map((item, index) => (
                  <SignUpComponent key={index} onClick={item?.action}>
                    <LogoSection image={item?.logo}></LogoSection>
                    <SignUpContentSection>
                      <Typography sx={{ color: "white", fontFamily: "Inter" }}>
                        {item?.title}
                      </Typography>
                    </SignUpContentSection>
                  </SignUpComponent>
                ))}
                <AppleLogin
                  clientId={process.env.REACT_APP_APPLE_CLIENT_ID}
                  redirectURI={process.env.REACT_APP_APPLE_REDIRECT_URI}
                  responseType={"code id_token"}
                  responseMode="form_post"
                  scope="email"
                  usePopup={false}
                  onSuccess={(response) =>
                    console.log("Apple Login Successful:", response)
                  }
                  onError={(error) =>
                    console.error("Apple Login Failed:", error)
                  }
                  render={(renderProps) => (
                    <SignUpComponent onClick={renderProps.onClick}>
                      <LogoSection image={apple}></LogoSection>
                      <SignUpContentSection>
                        <Typography
                          sx={{ color: "white", fontFamily: "Inter" }}
                        >
                          Continue with Apple{" "}
                        </Typography>
                      </SignUpContentSection>
                    </SignUpComponent>
                  )}
                />
                <SignUpAction>
                  <Typography
                    sx={{
                      color: "#ffffff85",
                      fontFamily: "Inter",
                      fontWeight: 300,
                      fontSize: "0.8rem",
                    }}
                  >
                    Don't have an account?
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate("/signup")}
                  >
                    Sign Up
                  </Button>
                  <Typography
                    sx={{
                      color: "white",
                      fontFamily: "Inter",
                      fontWeight: 300,
                      mt: 3,
                      cursor: "pointer",
                      fontSize: "0.8rem",
                    }}
                    onClick={() => navigate("/verify-invite")}
                  >
                    Manager / Admin Login
                  </Typography>
                  <Typography
                    sx={{
                      color: "#ffffff85",
                      fontFamily: "Inter",
                      fontWeight: 300,
                      mt: 2,
                      cursor: "pointer",
                      fontSize: "0.7rem",
                    }}
                    onClick={() => navigate("/")}
                  >
                    Back to home screen{" "}
                  </Typography>
                </SignUpAction>
              </SignUpSection>
            </>
          ) : activeStep === 1 ? (
            <>
              <Typography
                sx={{
                  fontSize: "1.7rem",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Enter Your Password
              </Typography>
              <Typography
                sx={{
                  color: "#ffffff85",
                  fontFamily: "Inter",
                  fontWeight: 300,
                  fontSize: "0.8rem",
                }}
              >
                {email || "exampleemail@gmail.com"}
              </Typography>
              <form onSubmit={handleLogin}>
                <FormControl fullWidth sx={{ marginTop: 2 }}>
                  <CustomFormLabel htmlFor="lastName">Password</CustomFormLabel>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    sx={{ marginBottom: 1 }}
                    placeholder="Password"
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ marginRight: 3 }}>
                          <IconButton
                            aria-label={
                              showPassword
                                ? "hide the password"
                                : "show the password"
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge="end"
                          >
                            {showPassword ? (
                              <Visibility sx={{ color: "#ffffff85" }} />
                            ) : (
                              <VisibilityOff sx={{ color: "#ffffff85" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="contained" type="submit">
                    Login
                  </Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleChange}
                        name="stayLoggedIn"
                        color="white"
                      />
                    }
                    sx={{ color: "white" }}
                    label="Stay Logged In"
                  />
                  <Stack
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    {" "}
                    <Typography
                      sx={{
                        color: "white",
                        fontFamily: "Inter",
                        fontWeight: 300,
                        mt: 5,
                        mb: 18,
                        cursor: "pointer",
                        fontSize: "0.8rem",
                      }}
                      onClick={() => navigate("/forgot-password")}
                    >
                      Forgot Password?
                    </Typography>
                  </Stack>
                </FormControl>
              </form>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "1.7rem",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Enter 2FA Code
              </Typography>
              <Typography
                sx={{
                  color: "#ffffff85",
                  fontFamily: "Inter",
                  fontWeight: 300,
                  fontSize: "0.8rem",
                  marginBottom: "16px",
                }}
              >
                Enter the 6-digit code from Google Authenticator
              </Typography>
              <form onSubmit={handle2FAVerification}>
                <FormControl fullWidth sx={{ marginTop: 2 }}>
                  <CustomFormLabel htmlFor="twoFACode">
                    Verification Code
                  </CustomFormLabel>
                  <TextField
                    type="text"
                    name="twoFACode"
                    value={twoFACode}
                    sx={{ marginBottom: 1 }}
                    placeholder="000000"
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      setTwoFACode(value);
                    }}
                    inputProps={{
                      maxLength: 6,
                      style: {
                        textAlign: "center",
                        fontSize: "1.5rem",
                        letterSpacing: "0.5rem",
                        fontFamily: "monospace",
                      },
                    }}
                  />
                  <Button variant="contained" type="submit">
                    Verify
                  </Button>
                  <Stack
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    <Typography
                      sx={{
                        color: "#ffffff85",
                        fontFamily: "Inter",
                        fontWeight: 300,
                        mt: 5,
                        fontSize: "0.8rem",
                      }}
                    >
                      Open Google Authenticator app to get the code
                    </Typography>
                  </Stack>
                </FormControl>
              </form>
            </>
          )}
        </LoginPageComponent>
      </LoginPageWrapper>
    </>
  );
};

export default LoginPage;
